import { startCase } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Inject, OnInit, AfterViewInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnCamera, SnDetection } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { t } from '@lib/translation';

@Component({
  selector: 'fs-detection-details-dialog',
  templateUrl: './detection-details-dialog.component.html',
  styleUrls: ['./detection-details-dialog.component.scss'],
})
export class DetectionDetailsDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  public isZoomed: boolean;
  public canvasId: string;
  public camera: SnCamera;
  public readonly isAvailable: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      detectionsImageUrl: string;
      detection: SnDetection;
      detectionFiltered: any;
      queryParams: any;
    },
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _toastrService: ToastrService,
  ) {
    this.canvasId = `zoomed-detection__${this.data.detection.id}`;
    this.isAvailable = this.data.detectionFiltered.metadata &&
      Object.keys(
        this.formatFields(this.data.detectionFiltered.metadata),
      ).length > 0;
    this._camerasAPI.getCamera(this.data.detection.cameraId)
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((camera) => {
        this.camera = camera;
      });
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this.data.detection.box
      ? this.isZoomed = true
      : this.isZoomed = false;
    this._resizeImage();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public isArray(item: any): boolean {
    return Array.isArray(item);
  }

  public isObject(item: any): boolean {
    return Object.prototype.toString.call(item).indexOf('Object') > -1;
  }

  public formatFields(data: Object): Object {
    if (data === undefined) { return; }
    if (data['type'] === 'detection') {
      if (data['vehicleSignature']) {
        delete data['vehicleSignature'];
      }
      if (data['trackerId']) {
        delete data['trackerId'];
      }
    }
    if (data['type'] === 'event') {
      return { ruleName: data['ruleName'] };
    }
    return data;
  }

  public toStartCase(field: string): string {
    return startCase(field);
  }

  public toggleImage(): void {
    this.isZoomed = !this.isZoomed;
    setTimeout(() => {
      this._resizeImage();
    }, 500);
    this._cd.detectChanges();
  }

  public incorrectSentryResult(): void {
    this._camerasAPI.flagAiDetection(
      this.data.detection.id,
      this.data.detectionFiltered.detectionId,
      this.data.queryParams,
    )
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: () => {
          this._toastrService.info(t('map.detectionDetails.reportReceived'));
        },
        error: () => {
          this._toastrService.error(t('map.detectionDetails.detectionAlreadyReported'));
        },
      });
  }

  public formatConfidence(confidence: number): number {
    return Math.round(confidence * 100) / 100;
  }

  private _resizeImage(): void {
    const canvas: any = document.getElementById(this.canvasId);

    const context: CanvasRenderingContext2D = canvas?.getContext('2d');
    const imageObj = new Image();

    if (!canvas) { return; }
    canvas.width = +this.data.detectionFiltered.box.width + 100;
    canvas.height = +this.data.detectionFiltered.box.height + 100;
    const maxX = this.data.detection.resolution.width;
    const maxY = this.data.detection.resolution.height;
    const maxWidth = maxX - this.data.detectionFiltered.box.startX;
    const maxHeight = maxY - this.data.detectionFiltered.box.startY;
    const height = Math.min(+this.data.detectionFiltered.box?.height + 100, maxHeight);
    const width = Math.min(+this.data.detectionFiltered.box?.width + 100, maxWidth);

    imageObj.onload = () => {
      context.drawImage(
        imageObj,
        this.data.detectionFiltered.box?.startX,
        this.data.detectionFiltered.box?.startY,
        width,
        height,
        0,
        0,
        width,
        height,
      );
    };
    imageObj.src = this.data.detectionsImageUrl;
  }
}
